import React, { useState, useEffect, useMemo, useCallback, memo } from "react";
import { useParams } from "react-router-dom";
import { Download } from "lucide-react";
import {
  processBomConsolidatedData,
  distributorSummary,
  findLowest,
  getExportRowsFromBomItems,
} from "utils/bomUtils";
import { getSlabUnitPrice } from "utils/bomToolUtils";
import { Button } from "components/ui/button";
import LoadingComponent from "components/LoadingComponent";
import BomSummaryView from "components/bom/BomSummaryView";
import BomResultProcessingTable from "components/bom/BomResultProcessingTable";
import ProgressBar from "components/ProgressBar";
import { END_POINT } from "constants/apiConstant";
import { DISTRIBUTORS } from "constants/common";
import { formatManufacturerName } from "utils/stringUtils";
import { capitalizeStr } from "utils/helper";
const { ARROW } = END_POINT;

const distributorsArr = [...DISTRIBUTORS, { name: "Verical", key: "verical", api: ARROW }];

const BomConsolidatedView = ({
  bomData,
  handleAddToCart,
  bomRefresh,
  handleBomDataDownload,
  onDistributorChange,
  setRenderCount,
  renderCount,
  pollComplete = false,
  pollAttempts = 0,
  maxPollCount = 10,
  ...rest
}) => {
  const { id } = useParams();
  const showPollProgress = pollAttempts > 0 || pollComplete;
  const progressPercent = maxPollCount > 0 ? Math.min(100, (pollAttempts / maxPollCount) * 100) : 0;

  const [all, setAll] = useState({
    distributors: [],
    products: [],
    allProducts: [],
    showLowest: false,
    distributorSummaryList: [],
    distributorSummaryTotal: 0,
    allDistributors: [],
  });

  const handleChipClick = (index) => {
    const updatedDistributor = all.distributors.map((item, itemIndex) =>
      index === itemIndex ? { ...item, status: !item.status } : item,
    );
    onLoad(all.showLowest, updatedDistributor);
  };

  const onLoad = (showLowestClick, distributors = null) => {
    const prcessedData = processBomConsolidatedData(bomData?.groupData);
    const { productsArray, productOriginalArray } = prcessedData;

    const distributorItems = distributorsArr.map((item) => {
      return { ...item, status: false };
    });

    let productArr;
    let singleDistproducts;
    if (distributors != null) {
      let selectedDistributors = distributors
        .filter((selected) => selected.status)
        .map((item) => item.key);
      let products = productsArray?.filter(
        (product) => !selectedDistributors.includes(product.api_partner),
      );
      //if the product is only produced by the selected distributor
      for (let i = 0; i < productsArray.length; i++) {
        let flag = false;
        for (let j = 0; j < productsArray.length; j++) {
          if (i != j) {
            if (
              productsArray[i].part_number.toLowerCase() ==
                productsArray[j].part_number.toLowerCase() &&
              parseInt(productsArray[i].qty) == parseInt(productsArray[j].qty)
            ) {
              if (
                productsArray[i].api_partner.toLowerCase() ==
                productsArray[j].api_partner.toLowerCase()
              ) {
                flag = true;
              } else {
                flag = false;
                break;
              }
            }
          }
        }
        if (flag) {
          if (selectedDistributors.includes(productsArray[i].api_partner)) {
            products = [...products, productsArray[i]];
          }
        }
      }
      productArr = products;
      //
      const uniqueObj = productsArray.reduce((acc, curr) => {
        const key = `${curr.part_number}-${curr.qty}`;
        if (!acc[key]) {
          acc[key] = curr;
        } else {
          acc[key].grade = 0;
        }
        return acc;
      }, {});
      const resultPro = Object.values(uniqueObj).filter((obj) => obj.grade !== 0);

      singleDistproducts = resultPro.filter((product) =>
        selectedDistributors.includes(product.api_partner),
      );
    } else {
      productArr = productsArray;
    }
    const partNumbersArr = productArr.map(function (value) {
      return value.part_number;
    });

    const distributorMinimumPrice = bomData.distributorMin;
    const uniquePartArr = [...new Set(partNumbersArr)];
    let uniquePartNumbersList = [];

    for (let i = 0; i < bomData.processedBom.length; i++) {
      let uniquePartNumbers = [];
      uniquePartNumbers["mpn"] = bomData.processedBom[i].part_number;
      uniquePartNumbers["qty"] = bomData.processedBom[i].qty;
      uniquePartNumbersList = [...uniquePartNumbersList, uniquePartNumbers];
    }

    //Lowest Product calculation
    let sortProArr = findLowest(uniquePartNumbersList, productArr);

    // for distributor summary calculation
    let distributorSummaryListArr = distributorSummary(sortProArr, distributorMinimumPrice);

    let summaryTotal = 0;
    for (let i = 0; i < distributorSummaryListArr.length; i++) {
      summaryTotal += parseFloat(distributorSummaryListArr[i].totalPrice);
    }

    if (distributors == null) {
      setAll({
        ...all,
        distributors: distributorItems,
        products: showLowestClick ? sortProArr : productsArray,
        showLowest: showLowestClick ? false : true,
        distributorSummaryList: distributorSummaryListArr,
        distributorSummaryTotal: summaryTotal,
        allDistributors: distributorItems,
        allProducts: productsArray,
      });
    } else {
      if (singleDistproducts.length > 0) {
        for (let i = 0; i < singleDistproducts.length; i++) {
          const price = getSlabUnitPrice(singleDistproducts[i].selectedPrice);
          let sortProduct = {
            ...singleDistproducts[i],
            totalPrice: parseInt(singleDistproducts[i].qty) * price,
          };
          sortProArr = [...sortProArr, sortProduct];
        }
      }
      distributorSummaryListArr = distributorSummary(sortProArr, distributorMinimumPrice);
      summaryTotal = 0;
      for (let i = 0; i < distributorSummaryListArr.length; i++) {
        summaryTotal += parseFloat(distributorSummaryListArr[i].totalPrice);
      }
      //if the Part number doesn't have any lowest price record then choosing the lowest from productArr(initial array)
      const MPNNotInSortArray = uniquePartNumbersList.filter(
        (obj1) => !sortProArr.some((obj2) => obj2.part_number == obj1.mpn && obj2.qty == obj1.qty),
      );
      const ProArr = productsArray.filter(
        (item) => item.api_partner != "tme" && item.api_partner != "rutronik",
      );
      const SortedMPN = findLowest(MPNNotInSortArray, ProArr);
      for (let i = 0; i < SortedMPN.length; i++) {
        sortProArr = [...sortProArr, SortedMPN[i]];
      }

      setAll({
        ...all,
        distributors: distributors,
        products: sortProArr,
        distributorSummaryList: distributorSummaryListArr,
        distributorSummaryTotal: summaryTotal,
        allProducts: productsArray,
      });
    }
  };

  const handleConsolidatedDownload = async () => {
    const headerRow = [
      "SKU",
      "Make",
      "Distributor",
      "Description",
      "HS Code",
      "Date Code",
      "RFQ QTY",
      "Stock",
      "MOQ",
      "MUL",
      "Price",
      "Status",
    ];

    let dataRows = [];
    if (bomData?.bomItems?.length) {
      dataRows = getExportRowsFromBomItems(bomData, all);
    }
    if (dataRows.length === 0 && all.products?.length) {
      dataRows = all.products.map((p) => [
        p.part_number,
        formatManufacturerName(p.manufacturer),
        capitalizeStr(p.api_partner),
        p.description ?? "",
        p.hts_code ?? "",
        p.date_code ?? "",
        p.qty ?? "",
        p.availability ?? "",
        p.selectedPrice?.min_qty ?? "",
        p.price?.multiple ?? "",
        p.selectedPrice ? getSlabUnitPrice(p.selectedPrice) : "",
        "",
      ]);
    }

    const arrayList = [headerRow, ...dataRows];
    const csvContent = arrayList
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const csvDataUri = `data:text/csv;charset=utf-8,${encodeURIComponent(csvContent)}`;
    const link = document.createElement("a");
    link.setAttribute("href", csvDataUri);
    link.setAttribute("download", "bom-consolidated.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    onLoad(true, null);
  }, [bomData?.groupData]);

  if (!bomData || bomData.loading)
    return <LoadingComponent message="Please wait, we are fetching the data" />;

  const progressLabel = pollComplete ? (
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-gray-600">BOM Result Processing Completed</span>
    </div>
  ) : (
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-gray-600">Processing BOM result...</span>
    </div>
  );

  return (
    <>
      <div className="w-full">
        <div className="mb-4 text-sm font-medium text-gray-700">Distributors</div>
        <div className="flex flex-wrap gap-2">
          {all.distributors.map((distributor, index) => (
            <div
              key={index}
              className="cursor-pointer px-4 py-1 rounded-lg border border-1 transition-all duration-200 shadow-sm hover:bg-gray-300"
              // onClick={() => handleChipClick(index)}
              style={{
                backgroundColor: distributor.status ? "#e5e7eb" : "#f9fafb",
                borderColor: distributor.status ? "#9ca3af" : "#d1d5db",
              }}
            >
              <span className="text-sm font-medium text-gray-700">{distributor.name}</span>
            </div>
          ))}
        </div>
      </div>
      <BomSummaryView bomData={bomData} all={all} />
      <div className="flex justify-end mt-6 gap-2">
        <Button variant="primary" size="sm" onClick={handleConsolidatedDownload}>
          <Download className="w-4 h-4 mr-2" />
          Download
        </Button>

        <Button variant="primary" size="sm" onClick={handleBomDataDownload}>
          <Download className="w-4 h-4 mr-2" />
          Download Raw Data
        </Button>
      </div>
      {/* {showPollProgress && (
        <ProgressBar
          label={progressLabel}
          progressPercent={progressPercent}
          subLabel={pollComplete ? "100%" : `${Math.round(progressPercent)}%`}
        />
      )} */}
      <BomResultProcessingTable
        bomData={bomData}
        all={all}
        handleAddToCart={handleAddToCart}
        bomRefresh={bomRefresh}
        onDistributorChange={onDistributorChange}
        setRenderCount={setRenderCount}
        renderCount={renderCount}
        pollComplete={pollComplete}
        pollAttempts={pollAttempts}
        maxPollCount={maxPollCount}
        {...rest}
      />
    </>
  );
};

export default BomConsolidatedView;
